import styled from 'styled-components';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { theme } from '../styles/theme';
import { PulseHero } from '../components/pulse/PulseHero';
import { QuickStart } from '../components/pulse/QuickStart';
import { LiveContexts } from '../components/pulse/LiveContexts';
import { SocialLinks } from '../components/ui/SocialLinks';
import { Navigation } from '../components/ui/Navigation';
import { useAccount } from '../components/WalletProvider';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';

const PulseView = styled.main`
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    ${theme.colors.washi} 0%,
    ${theme.colors.washi} 50%,
    rgba(64, 145, 220, 0.05) 100%
  );
  overflow-y: auto;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
  position: relative;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #4091DC;
  color: ${theme.colors.washi};
  text-decoration: none;
  border-radius: 20px;
  font-family: 'M PLUS Rounded 1c', sans-serif;
  font-weight: 600;
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(64, 145, 220, 0.2);
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-2px);
    background: #3584cf;
    box-shadow: 0 4px 12px rgba(64, 145, 220, 0.3);
  }

  &::before {
    content: '←';
    font-size: 1.2em;
  }
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div`
  position: sticky;
  top: 24px;
  height: fit-content;
`;

const MainArea = styled.div`
  min-width: 0; // Prevent grid blowout
`;

const SocialIconsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-right: 16px;
  border-right: 1px solid rgba(0, 0, 0, 0.1);
`;

const WalletLink = styled.button`
  background: linear-gradient(135deg, #FFB7C5 0%, #ff9eb1 100%);
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  font: inherit;
  color: #FFFFFF;
  font-weight: 700;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  box-shadow: 0 2px 8px rgba(255, 183, 197, 0.3);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 183, 197, 0.4);
    background: linear-gradient(135deg, #ff9eb1 0%, #ff85a1 100%);
  }
`;

export function Pulse() {
  const { account } = useAccount();
  const { setVisible } = useWalletModal();

  useEffect(() => {
    document.title = 'Pulse - Realtime Agent Context';
  }, []);

  return (
    <PulseView>
      <Container>
        <TopBar>
          <TopBarLeft>
            <BackButton to="/">Back to Chat</BackButton>
            <PulseHero />
          </TopBarLeft>
          <TopBarRight>
            <SocialIconsGroup>
              <SocialLinks hideDiscord />
            </SocialIconsGroup>
            {!account ? (
              <WalletLink onClick={() => setVisible(true)}>Connect Wallet</WalletLink>
            ) : (
              <Navigation />
            )}
          </TopBarRight>
        </TopBar>
        <MainContent>
          <Sidebar>
            <QuickStart />
          </Sidebar>
          <MainArea>
            <LiveContexts />
          </MainArea>
        </MainContent>
      </Container>
    </PulseView>
  );
}

export default Pulse; 